import api from "@/lib/axiosConfig";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

const baseRoute = "back/api/sculpture-edition-history";

const _sculptureEditionHistorySchema = z.object({
  id: z.string(),
  sculptureEditionId: z.string(),
  instanceId: z.string(),
  description: z.string(),
  villePays: z.string(),
  dates: z.string(),
  collectionParticuliere: z.boolean(),
  sequence: z.number(),
  provenanceRank: z.number().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type SculptureEditionHistory = z.infer<
  typeof _sculptureEditionHistorySchema
>;

const _createSculptureEditionHistorySchema =
  _sculptureEditionHistorySchema.omit({
    id: true,
    instanceId: true,
    sequence: true,
    createdAt: true,
    updatedAt: true,
  });

export type CreateSculptureEditionHistory = z.infer<
  typeof _createSculptureEditionHistorySchema
>;

export type EditSculptureEditionHistory = Omit<
  CreateSculptureEditionHistory,
  "sculptureEditionId"
>;

export type SculptureEditionHistoryMergedAuction = {
  kind: "auction";
  id: string;
  sculptureEditionId: string;
  instanceId: string;
  auctionId: string | null;
  provenanceRank?: number;
  auction: {
    id: string;
    ventesDescriptionNumeroVente: string;
    ventesDescriptionTitre: string;
    ventesDescriptionCommissaires: string;
    ventesDescriptionDate: string;
    ventesDescriptionLieu: string;
    ventesDescriptionVille: string;
    ventesDescriptionPays: string;
  } | null;
  sculptureEditionDansVentesNumeroLot: string;
  sculptureEditionDansVentesNotes: string;
  createdAt?: string;
  updatedAt?: string | null;
};

/**
 * Provenance: timeline triée (historique + ventes), ordre persisté via provenanceRank.
 */
export type SculptureEditionHistoryMerged = {
  timeline: Array<
    (SculptureEditionHistory & { kind: "history" }) | SculptureEditionHistoryMergedAuction
  >;
};

const historyQueryKey = (
  sculptureEditionId: string | undefined,
  includeAuctions: boolean,
) => [
  "sculpture-edition-history",
  sculptureEditionId,
  includeAuctions ? "withAuctions" : "only",
];

// ─── GET LIST ────────────────────────────────────────────────────────────────

async function getSculptureEditionHistoryOnly(sculptureEditionId: string) {
  const { data } = await api.get<SculptureEditionHistory[]>(baseRoute, {
    params: { sculptureEditionId },
  });
  return data;
}

async function getSculptureEditionHistoryMerged(sculptureEditionId: string) {
  const { data } = await api.get<SculptureEditionHistoryMerged>(baseRoute, {
    params: { sculptureEditionId, includeAuctions: "true" },
  });
  return data;
}

export function useGetSculptureEditionHistory(
  sculptureEditionId: string | undefined,
) {
  return useQuery({
    queryKey: historyQueryKey(sculptureEditionId, false),
    queryFn: () =>
      sculptureEditionId
        ? getSculptureEditionHistoryOnly(sculptureEditionId)
        : Promise.resolve([] as SculptureEditionHistory[]),
    enabled: !!sculptureEditionId,
  });
}

export function useGetSculptureEditionHistoryMerged(
  sculptureEditionId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: historyQueryKey(sculptureEditionId, true),
    queryFn: () =>
      sculptureEditionId
        ? getSculptureEditionHistoryMerged(sculptureEditionId)
        : Promise.resolve({
            timeline: [],
          } satisfies SculptureEditionHistoryMerged),
    enabled: !!sculptureEditionId && enabled,
  });
}

function invalidateHistory(
  queryClient: ReturnType<typeof useQueryClient>,
  sculptureEditionId: string,
) {
  queryClient.invalidateQueries({
    queryKey: ["sculpture-edition-history", sculptureEditionId],
  });
}

// ─── CREATE ──────────────────────────────────────────────────────────────────

async function createSculptureEditionHistory(
  params: CreateSculptureEditionHistory,
) {
  const { data } = await api.post<SculptureEditionHistory>(baseRoute, params);
  return data;
}

export function useCreateSculptureEditionHistory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSculptureEditionHistory,
    onSuccess: (_, { sculptureEditionId }) =>
      invalidateHistory(queryClient, sculptureEditionId),
  });
}

// ─── UPDATE ──────────────────────────────────────────────────────────────────

async function editSculptureEditionHistory({
  historyId,
  history,
}: {
  historyId: string;
  sculptureEditionId: string;
  history: EditSculptureEditionHistory;
}) {
  const { data } = await api.put<SculptureEditionHistory>(
    `${baseRoute}/${historyId}`,
    history,
  );
  return data;
}

export function useEditSculptureEditionHistory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editSculptureEditionHistory,
    onSuccess: (_, { sculptureEditionId }) =>
      invalidateHistory(queryClient, sculptureEditionId),
  });
}

// ─── DELETE ──────────────────────────────────────────────────────────────────

async function deleteSculptureEditionHistory({
  historyId,
}: {
  historyId: string;
  sculptureEditionId: string;
}) {
  await api.delete(`${baseRoute}/${historyId}`);
}

export function useDeleteSculptureEditionHistory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSculptureEditionHistory,
    onSuccess: (_, { sculptureEditionId }) =>
      invalidateHistory(queryClient, sculptureEditionId),
  });
}

// ─── REORDER (optimistic) ────────────────────────────────────────────────────

async function updateSculptureEditionHistorySequence({
  newSequence,
  sculptureEditionId,
}: {
  newSequence: string[];
  sculptureEditionId: string;
}) {
  await api.put(`${baseRoute}/sequence`, {
    sculptureEditionId,
    sequence: newSequence,
  });
}

export function useUpdateSculptureEditionHistorySequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateSculptureEditionHistorySequence,
    onMutate: async ({ newSequence, sculptureEditionId }) => {
      const key = historyQueryKey(sculptureEditionId, false);
      await queryClient.cancelQueries({ queryKey: key });

      const previous =
        queryClient.getQueryData<SculptureEditionHistory[]>(key);

      if (previous) {
        const byId = new Map(previous.map((h) => [h.id, h]));
        const reordered = newSequence
          .map((id, index) => {
            const h = byId.get(id);
            return h ? { ...h, sequence: index } : undefined;
          })
          .filter((h): h is SculptureEditionHistory => h !== undefined);
        queryClient.setQueryData(key, reordered);
      }

      return { previous };
    },
    onError: (_err, { sculptureEditionId }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          historyQueryKey(sculptureEditionId, false),
          context.previous,
        );
      }
    },
  });
}

async function reorderProvenanceTimeline({
  sculptureEditionId,
  timeline,
}: {
  sculptureEditionId: string;
  timeline: { kind: "history" | "auction"; id: string }[];
}) {
  await api.put(`${baseRoute}/provenance-sequence`, {
    sculptureEditionId,
    timeline,
  });
}

export function useReorderSculptureEditionProvenance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reorderProvenanceTimeline,
    onSuccess: (_, { sculptureEditionId }) =>
      invalidateHistory(queryClient, sculptureEditionId),
  });
}
